import { useEffect, useState, useCallback } from "react";
import PageTour from "@/components/PageTour";
import { api } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Workflow as WorkflowIcon, Plus, Loader2, Trash2, X, Check, Bell, Mail,
  ClipboardList, AlertTriangle, Clock, ShieldCheck, ArrowRight, Zap,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const TRIGGERS = [
  { key: "email_in", label: "Email in arrivo", icon: Mail, group: "email" },
  { key: "pratica_created", label: "Nuova pratica", icon: ClipboardList, group: "pratica" },
  { key: "pratica_status_changed", label: "Cambio stato pratica", icon: ArrowRight, group: "pratica" },
  { key: "pratica_sla_overdue", label: "SLA / scadenza superata", icon: Clock, group: "pratica" },
  { key: "pratica_supplier_no_response", label: "Fornitore non risponde", icon: AlertTriangle, group: "pratica" },
];
const TRIGGER_LABEL = Object.fromEntries(TRIGGERS.map((t) => [t.key, t.label]));

const PRIORITIES = [["alta", "Alta"], ["media", "Media"], ["bassa", "Bassa"]];
const URGENCIES = [["emergenza", "Emergenza"], ["alta", "Alta"], ["normale", "Normale"], ["bassa", "Bassa"]];
const STATUSES = [
  ["nuova", "Nuova"], ["da_verificare", "Da verificare"], ["in_attesa_informazioni", "In attesa informazioni"],
  ["fornitore_da_individuare", "Fornitore da individuare"], ["richiesta_inviata", "Richiesta inviata"],
  ["in_attesa_fornitore", "In attesa fornitore"], ["intervento_programmato", "Intervento programmato"],
  ["in_attesa_preventivo", "In attesa preventivo"], ["risolta", "Risolta"], ["chiusa", "Chiusa"],
  ["sospesa", "Sospesa"], ["annullata", "Annullata"],
];

// Campi per trigger email vs pratica
const EMAIL_FIELDS = [
  { key: "sender", label: "Mittente", type: "text" },
  { key: "subject", label: "Oggetto", type: "text" },
  { key: "text", label: "Oggetto + testo", type: "text" },
  { key: "category", label: "Categoria", type: "category" },
  { key: "priority", label: "Priorità", type: "priority" },
  { key: "building_id", label: "Condominio", type: "building" },
  { key: "has_attachments", label: "Ha allegati", type: "bool" },
];
const PRATICA_FIELDS = [
  { key: "text", label: "Oggetto / problema", type: "text" },
  { key: "category", label: "Categoria", type: "category" },
  { key: "urgency", label: "Urgenza", type: "urgency" },
  { key: "status", label: "Stato", type: "status" },
  { key: "priority", label: "Priorità", type: "priority" },
  { key: "building_id", label: "Condominio", type: "building" },
];

const OPS_BY_TYPE = {
  text: [["contains", "contiene"], ["not_contains", "non contiene"], ["equals", "è"], ["not_equals", "non è"]],
  category: [["equals", "è"], ["not_equals", "non è"]],
  priority: [["equals", "è"], ["not_equals", "non è"]],
  urgency: [["equals", "è"], ["not_equals", "non è"]],
  status: [["equals", "è"], ["not_equals", "non è"]],
  building: [["equals", "è"], ["not_equals", "non è"]],
  bool: [["is_true", "sì"], ["is_false", "no"]],
};

// Azioni. proposal=true → richiede approvazione (Livello 1). scope: email/pratica/both
const ACTIONS = [
  { key: "open_pratica", label: "Apri pratica", scope: "email", params: [] },
  { key: "set_priority", label: "Imposta priorità", scope: "both", params: ["priority"] },
  { key: "set_category", label: "Imposta categoria", scope: "both", params: ["category"] },
  { key: "add_note", label: "Aggiungi nota", scope: "pratica", params: ["text"] },
  { key: "notify", label: "Notifica interna", scope: "both", params: ["text"] },
  { key: "forward_email", label: "Inoltra email", scope: "email", proposal: true, params: ["to"] },
  { key: "assign_supplier", label: "Assegna fornitore suggerito", scope: "pratica", proposal: true, params: [] },
  { key: "draft_condomino_update", label: "Aggiornamento al condòmino", scope: "pratica", proposal: true, params: ["text"] },
];
const ACTION_LABEL = Object.fromEntries(ACTIONS.map((a) => [a.key, a.label]));
const ACTION_PROPOSAL = Object.fromEntries(ACTIONS.map((a) => [a.key, !!a.proposal]));

const emptyWorkflow = () => ({
  name: "", enabled: true, trigger: "email_in", match: "all",
  conditions: [{ field: "text", op: "contains", value: "" }],
  actions: [{ type: "notify", params: { text: "" } }],
});

export default function WorkflowBuilder() {
  const { catLabel, categories } = useCategories();
  const [workflows, setWorkflows] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busyProp, setBusyProp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, b, p, s, n] = await Promise.all([
        api.get("/workflows"), api.get("/buildings"),
        api.get("/workflows/proposals", { params: { status: "pending" } }),
        api.get("/workflows/stats"), api.get("/workflows/notifications"),
      ]);
      setWorkflows(w.data); setBuildings(b.data); setProposals(p.data);
      setStats(s.data); setNotifications(n.data);
    } catch { toast.error("Errore nel caricamento dei workflow"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Tour guidato: crea un workflow DEMO se serve e lo elimina a fine tour ---
  const startTourDemo = useCallback(async () => {
    try { await api.post("/tour-demo/workflow"); await load(); }
    catch (e) { console.debug("tour demo workflow:", e); }
  }, [load]);
  const endTourDemo = useCallback(async () => {
    try { await api.delete("/tour-demo/workflow"); } catch (e) { console.debug("tour demo del:", e); }
    load();
  }, [load]);

  const WORKFLOW_TOUR_STEPS = [
    { title: "Automazioni & Workflow ⚙️", body: "Ti mostro come funzionano le automazioni. Ho aggiunto un workflow di esempio per la guida — lo elimino alla fine. Esci con la X, riapri col pulsante \"Guida\"." },
    { selector: "new-workflow-button", title: "Crea un workflow", body: "Definisci una regola SE… (es. arriva un'email di una certa categoria) ALLORA… (es. crea pratica, imposta priorità, proponi un fornitore)." },
    { selector: "workflow-card-0", title: "Un workflow", body: "Ogni scheda mostra la regola: il trigger, le condizioni (SE) e le azioni (ALLORA). Puoi attivarlo/disattivarlo, modificarlo o eliminarlo." },
    { selector: "workflow-stats", title: "Monitoraggio", body: "Qui vedi a colpo d'occhio le automazioni attive, le esecuzioni totali e le proposte in attesa di approvazione." },
    { title: "Sempre sotto il tuo controllo 🛡️", body: "Le azioni sensibili come invii o incarichi non partono da sole: diventano proposte che tu approvi. L'AI prepara, tu autorizzi." },
    { title: "Pronto! 🎉", body: "Elimino il workflow di esempio. Riavvia questa guida quando vuoi dal pulsante \"Guida\" in alto." },
  ];

  const isEmail = (trig) => TRIGGERS.find((t) => t.key === trig)?.group === "email";
  const fieldsFor = (trig) => (isEmail(trig) ? EMAIL_FIELDS : PRATICA_FIELDS);
  const actionsFor = (trig) => ACTIONS.filter((a) => a.scope === "both" || a.scope === (isEmail(trig) ? "email" : "pratica"));
  const fieldMeta = (trig, key) => fieldsFor(trig).find((f) => f.key === key) || fieldsFor(trig)[0];

  const openNew = () => setEditing(emptyWorkflow());
  const openEdit = (wf) => setEditing(JSON.parse(JSON.stringify(wf)));

  const setTrigger = (trig) => {
    const flds = fieldsFor(trig);
    const acts = actionsFor(trig);
    setEditing((e) => ({
      ...e, trigger: trig,
      conditions: [{ field: flds[0].key, op: OPS_BY_TYPE[flds[0].type][0][0], value: "" }],
      actions: [{ type: acts[0].key, params: {} }],
    }));
  };

  // ---- conditions ----
  const addCond = () => setEditing((e) => {
    const flds = fieldsFor(e.trigger);
    return { ...e, conditions: [...e.conditions, { field: flds[0].key, op: OPS_BY_TYPE[flds[0].type][0][0], value: "" }] };
  });
  const removeCond = (i) => setEditing((e) => ({ ...e, conditions: e.conditions.filter((_, idx) => idx !== i) }));
  const setCond = (i, patch) => setEditing((e) => ({
    ...e, conditions: e.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
  }));
  const setCondField = (i, field) => {
    const t = fieldMeta(editing.trigger, field).type;
    setCond(i, { field, op: OPS_BY_TYPE[t][0][0], value: "" });
  };

  // ---- actions ----
  const addAction = () => setEditing((e) => {
    const acts = actionsFor(e.trigger);
    return { ...e, actions: [...e.actions, { type: acts[0].key, params: {} }] };
  });
  const removeAction = (i) => setEditing((e) => ({ ...e, actions: e.actions.filter((_, idx) => idx !== i) }));
  const setActionType = (i, type) => setEditing((e) => ({
    ...e, actions: e.actions.map((a, idx) => (idx === i ? { type, params: {} } : a)),
  }));
  const setActionParam = (i, k, v) => setEditing((e) => ({
    ...e, actions: e.actions.map((a, idx) => (idx === i ? { ...a, params: { ...a.params, [k]: v } } : a)),
  }));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Inserisci un nome per il workflow");
    if (!editing.actions.length) return toast.error("Aggiungi almeno un'azione");
    for (const a of editing.actions) {
      if (a.type === "forward_email" && !(a.params.to || "").trim())
        return toast.error("Specifica l'email di inoltro per l'azione 'Inoltra email'");
    }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/workflows/${editing.id}`, editing);
      else await api.post("/workflows", editing);
      toast.success("Workflow salvato");
      setEditing(null);
      await load();
    } catch { toast.error("Errore nel salvataggio del workflow"); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async (wf) => {
    try { await api.put(`/workflows/${wf.id}`, { ...wf, enabled: !wf.enabled }); await load(); }
    catch { toast.error("Errore nell'aggiornamento"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/workflows/${toDelete.id}`); toast.success("Workflow eliminato"); setToDelete(null); await load(); }
    catch { toast.error("Errore durante l'eliminazione"); }
  };

  const approveProp = async (p) => {
    setBusyProp(p.id);
    try { const r = await api.post(`/workflows/proposals/${p.id}/approve`); toast.success(r.data.detail || "Approvato"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'approvazione"); }
    finally { setBusyProp(null); }
  };
  const rejectProp = async (p) => {
    setBusyProp(p.id);
    try { await api.post(`/workflows/proposals/${p.id}/reject`); toast.success("Proposta rifiutata"); await load(); }
    catch { toast.error("Errore"); }
    finally { setBusyProp(null); }
  };

  const buildingName = (id) => buildings.find((b) => b.id === id)?.name || id;
  const valueLabel = (trig, c) => {
    const t = fieldMeta(trig, c.field).type;
    if (t === "bool") return "";
    if (t === "category") return catLabel(c.value) || c.value;
    if (t === "building") return buildingName(c.value);
    if (t === "status") return (STATUSES.find((s) => s[0] === c.value) || [])[1] || c.value;
    return c.value;
  };
  const opLabel = (trig, c) => {
    const t = fieldMeta(trig, c.field).type;
    return (OPS_BY_TYPE[t].find((o) => o[0] === c.op) || [])[1] || c.op;
  };

  const renderValueEditor = (i, c) => {
    const meta = fieldMeta(editing.trigger, c.field);
    const t = meta.type;
    if (t === "bool") return null;
    if (t === "text")
      return <Input data-testid={`cond-value-${i}`} value={c.value} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="valore…" className="rounded-md" />;
    const opts = t === "category" ? (categories.filter((x) => x.key !== "altro").map((x) => [x.key, catLabel(x.key) || x.key]))
      : t === "priority" ? PRIORITIES
      : t === "urgency" ? URGENCIES
      : t === "status" ? STATUSES
      : buildings.map((b) => [b.id, b.name]);
    return (
      <Select value={c.value || ""} onValueChange={(v) => setCond(i, { value: v })}>
        <SelectTrigger data-testid={`cond-value-${i}`} className="rounded-md"><SelectValue placeholder="Scegli…" /></SelectTrigger>
        <SelectContent>{opts.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
    );
  };

  const renderActionParams = (i, a) => {
    const spec = ACTIONS.find((x) => x.key === a.type);
    if (!spec) return null;
    return spec.params.map((p) => {
      if (p === "priority")
        return (
          <Select key={p} value={a.params.priority || "media"} onValueChange={(v) => setActionParam(i, "priority", v)}>
            <SelectTrigger data-testid={`action-priority-${i}`} className="rounded-md mt-2"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        );
      if (p === "category")
        return (
          <Select key={p} value={a.params.category || ""} onValueChange={(v) => setActionParam(i, "category", v)}>
            <SelectTrigger data-testid={`action-category-${i}`} className="rounded-md mt-2"><SelectValue placeholder="Categoria…" /></SelectTrigger>
            <SelectContent>{categories.filter((x) => x.key !== "altro").map((x) => <SelectItem key={x.key} value={x.key}>{catLabel(x.key) || x.key}</SelectItem>)}</SelectContent>
          </Select>
        );
      if (p === "to")
        return <Input key={p} data-testid={`action-to-${i}`} value={a.params.to || ""} onChange={(e) => setActionParam(i, "to", e.target.value)} placeholder="collega@studio.it" className="rounded-md mt-2" />;
      if (p === "text")
        return <Textarea key={p} data-testid={`action-text-${i}`} value={a.params.text || ""} onChange={(e) => setActionParam(i, "text", e.target.value)} placeholder={a.type === "draft_condomino_update" ? "Testo aggiornamento (vuoto = generato automaticamente)" : "Testo…"} className="rounded-md mt-2 min-h-[64px]" />;
      return null;
    });
  };

  const propIcon = (t) => (t === "forward_email" ? Mail : t === "assign_supplier" ? ShieldCheck : Bell);

  return (
    <div className="fade-in" data-testid="workflow-page">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Automazioni · Direttore Operativo AI</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-head text-4xl font-extrabold tracking-tighter">Workflow</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">Costruisci regole <strong>SE… ALLORA…</strong> su email e pratiche. Le azioni sensibili (invii/incarichi) restano <strong>proposte da approvare</strong> (Livello 1): l'AI prepara, tu autorizzi.</p>
            <PageTour storageKey="tour_workflow" className="mt-3" steps={WORKFLOW_TOUR_STEPS} onOpen={startTourDemo} onClose={endTourDemo} />
          </div>
          <Button data-testid="new-workflow-button" onClick={openNew} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-11 shrink-0">
            <Plus className="h-4 w-4 mr-2" />Nuovo workflow
          </Button>
        </div>
      </header>

      <div className="p-4 sm:p-10 space-y-6">
        {stats && (
          <div data-testid="workflow-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Workflow attivi</div>
              <div className="font-head text-3xl font-extrabold tracking-tighter mt-1">{stats.enabled}<span className="text-lg text-muted-foreground">/{stats.total}</span></div>
            </div>
            <div className="bg-[#0A0A0A] text-white rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Esecuzioni totali</div>
              <div className="font-head text-3xl font-extrabold tracking-tighter mt-1">{stats.total_runs}</div>
            </div>
            <div className="bg-card border border-border rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Proposte in attesa</div>
              <div className="font-head text-3xl font-extrabold tracking-tighter mt-1 text-[#007BFF]">{stats.pending_proposals}</div>
            </div>
            <div className="bg-card border border-border rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Bell className="h-3 w-3" />Notifiche</div>
              <div className="font-head text-3xl font-extrabold tracking-tighter mt-1">{notifications.length}</div>
            </div>
          </div>
        )}

        {/* Proposte da approvare */}
        {proposals.length > 0 && (
          <div data-testid="proposals-section" className="bg-card border-2 border-[#007BFF]/40 rounded-md p-6">
            <h3 className="font-head text-lg font-semibold mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#007BFF]" />Proposte da approvare</h3>
            <p className="text-sm text-muted-foreground mb-4">Azioni preparate dai workflow che richiedono la tua autorizzazione prima dell'invio.</p>
            <div className="space-y-3">
              {proposals.map((p) => {
                const Ico = propIcon(p.action_type);
                return (
                  <div key={p.id} data-testid={`proposal-${p.id}`} className="flex items-start gap-3 border border-border rounded-md p-3 bg-secondary/30">
                    <div className="h-9 w-9 rounded-md bg-[#007BFF]/10 text-[#007BFF] flex items-center justify-center shrink-0"><Ico className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm">{p.label || ACTION_LABEL[p.action_type]}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Workflow: {p.workflow_name} · {TRIGGER_LABEL[p.trigger] || p.trigger}</div>
                      {p.generated?.body && <div className="text-xs text-foreground/80 mt-1.5 line-clamp-2 border-l-2 border-[#007BFF]/40 pl-2 whitespace-pre-wrap">{p.generated.body}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button data-testid={`approve-${p.id}`} size="sm" disabled={busyProp === p.id} onClick={() => approveProp(p)} className="rounded-md bg-emerald-600 hover:bg-emerald-700 h-8">
                        {busyProp === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" />Approva</>}
                      </Button>
                      <Button data-testid={`reject-${p.id}`} size="sm" variant="outline" disabled={busyProp === p.id} onClick={() => rejectProp(p)} className="rounded-md h-8">
                        <X className="h-3.5 w-3.5 mr-1" />Rifiuta
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Lista workflow */}
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Caricamento…</div>
        ) : workflows.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3">
            <WorkflowIcon className="h-10 w-10 opacity-40" />
            Nessun workflow configurato. Crea il tuo primo automatismo.
          </div>
        ) : (
          <div data-testid="workflows-list" className="grid gap-4 md:grid-cols-2">
            {workflows.map((wf, i) => {
              const Ico = (TRIGGERS.find((t) => t.key === wf.trigger) || {}).icon || Zap;
              return (
                <div key={wf.id} data-testid={`workflow-card-${i}`} className="list-enter bg-card border border-border rounded-md p-5" style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}>
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#007BFF]/10 text-[#007BFF] flex items-center justify-center shrink-0"><Ico className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{wf.name}</span>
                        {!wf.enabled && <span className="text-[10px] uppercase font-bold text-muted-foreground bg-secondary rounded px-1.5 py-0.5">off</span>}
                        <span className="text-[10px] font-semibold text-[#007BFF] bg-[#007BFF]/10 rounded-full px-2 py-0.5">{wf.run_count || 0} esec.</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Ico className="h-3 w-3" />Quando: {TRIGGER_LABEL[wf.trigger]}</div>
                    </div>
                    <Switch data-testid={`workflow-toggle-${i}`} checked={wf.enabled} onCheckedChange={() => toggleEnabled(wf)} />
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase font-bold text-muted-foreground">Se {wf.match === "all" ? "tutte" : "almeno una"}</span>
                      {(wf.conditions || []).map((c, k) => (
                        <span key={k} className="text-[11px] bg-secondary text-secondary-foreground rounded px-1.5 py-0.5">
                          {(fieldsFor(wf.trigger).find((f) => f.key === c.field) || {}).label || c.field} {opLabel(wf.trigger, c)} {valueLabel(wf.trigger, c) && <strong>{valueLabel(wf.trigger, c)}</strong>}
                        </span>
                      ))}
                      {(!wf.conditions || wf.conditions.length === 0) && <span className="text-[11px] text-muted-foreground">sempre</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase font-bold text-muted-foreground">Allora</span>
                      {(wf.actions || []).map((a, k) => (
                        <span key={k} className={`text-[11px] rounded px-1.5 py-0.5 flex items-center gap-1 ${ACTION_PROPOSAL[a.type] ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" : "bg-[#007BFF]/10 text-[#007BFF]"}`}>
                          {ACTION_LABEL[a.type] || a.type}{ACTION_PROPOSAL[a.type] && <ShieldCheck className="h-3 w-3" title="Richiede approvazione" />}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
                    <Button data-testid={`workflow-edit-${i}`} variant="ghost" size="sm" onClick={() => openEdit(wf)} className="rounded-md">Modifica</Button>
                    <button data-testid={`workflow-delete-${i}`} onClick={() => setToDelete(wf)} className="text-muted-foreground hover:text-red-600 transition-colors duration-150 p-1.5 ml-auto" aria-label="Elimina workflow"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Notifiche recenti */}
        {notifications.length > 0 && (
          <div data-testid="workflow-notifications" className="bg-card border border-border rounded-md p-6">
            <h3 className="font-head text-lg font-semibold mb-4 flex items-center gap-2"><Bell className="h-4 w-4 text-[#007BFF]" />Notifiche recenti</h3>
            <div className="space-y-2.5">
              {notifications.slice(0, 12).map((n) => (
                <div key={n.id} className="flex items-center gap-3 text-sm border-l-2 border-[#007BFF] pl-3">
                  <span className="font-medium text-foreground truncate max-w-[45%]">{n.message}</span>
                  <span className="truncate flex-1 text-muted-foreground">{n.subject ? `— ${n.subject}` : ""} <span className="text-xs">(workflow: {n.workflow_name})</span></span>
                  <span className="text-[11px] text-muted-foreground shrink-0">{new Date(n.ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="workflow-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifica workflow" : "Nuovo workflow"}</DialogTitle>
            <DialogDescription className="sr-only">Configura trigger, condizioni e azioni.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5 py-1">
              <div>
                <Label className="text-xs font-semibold">Nome</Label>
                <Input data-testid="workflow-name-input" value={editing.name} onChange={(e) => setEditing((r) => ({ ...r, name: e.target.value }))} placeholder="Es. Guasto ascensore → tecnico" className="rounded-md mt-1" />
              </div>

              <div>
                <Label className="text-xs font-semibold">Quando (trigger)</Label>
                <Select value={editing.trigger} onValueChange={setTrigger}>
                  <SelectTrigger data-testid="workflow-trigger-select" className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Condizioni */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Condizioni (SE…)</div>
                  <Select value={editing.match} onValueChange={(v) => setEditing((e) => ({ ...e, match: v }))}>
                    <SelectTrigger data-testid="workflow-match-select" className="rounded-md h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte (E)</SelectItem>
                      <SelectItem value="any">Almeno una (O)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  {editing.conditions.map((c, i) => {
                    const meta = fieldMeta(editing.trigger, c.field);
                    return (
                      <div key={i} data-testid={`condition-row-${i}`} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                        <Select value={c.field} onValueChange={(v) => setCondField(i, v)}>
                          <SelectTrigger data-testid={`cond-field-${i}`} className="rounded-md"><SelectValue /></SelectTrigger>
                          <SelectContent>{fieldsFor(editing.trigger).map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={c.op} onValueChange={(v) => setCond(i, { op: v })}>
                          <SelectTrigger data-testid={`cond-op-${i}`} className="rounded-md w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>{OPS_BY_TYPE[meta.type].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                        <div>{renderValueEditor(i, c)}</div>
                        <button data-testid={`cond-remove-${i}`} onClick={() => removeCond(i)} className="text-muted-foreground hover:text-red-600 p-1"><X className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                </div>
                <Button data-testid="add-condition-button" variant="outline" size="sm" onClick={addCond} className="rounded-md mt-2 h-8"><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi condizione</Button>
              </div>

              {/* Azioni */}
              <div className="border-t border-border pt-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">Azioni (ALLORA…)</div>
                <div className="space-y-2">
                  {editing.actions.map((a, i) => (
                    <div key={i} data-testid={`action-row-${i}`} className="border border-border rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <Select value={a.type} onValueChange={(v) => setActionType(i, v)}>
                          <SelectTrigger data-testid={`action-type-${i}`} className="rounded-md flex-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{actionsFor(editing.trigger).map((x) => <SelectItem key={x.key} value={x.key}>{x.label}{x.proposal ? " (approvazione)" : ""}</SelectItem>)}</SelectContent>
                        </Select>
                        <button data-testid={`action-remove-${i}`} onClick={() => removeAction(i)} className="text-muted-foreground hover:text-red-600 p-1"><X className="h-4 w-4" /></button>
                      </div>
                      {ACTION_PROPOSAL[a.type] && <div className="text-[11px] text-amber-600 mt-1.5 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Crea una proposta da approvare (nessun invio automatico)</div>}
                      {renderActionParams(i, a)}
                    </div>
                  ))}
                </div>
                <Button data-testid="add-action-button" variant="outline" size="sm" onClick={addAction} className="rounded-md mt-2 h-8"><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi azione</Button>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <Switch data-testid="workflow-enabled-switch" checked={editing.enabled} onCheckedChange={(v) => setEditing((r) => ({ ...r, enabled: v }))} />
                <span className="text-sm">Workflow attivo</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">Annulla</Button>
            <Button data-testid="workflow-save-button" onClick={save} disabled={saving} className="rounded-md bg-[#007BFF] hover:bg-[#0069D9]">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvataggio…</> : "Salva workflow"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-workflow-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il workflow?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" verrà eliminato definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-md">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-workflow" onClick={doDelete} className="rounded-md bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
